// Canonical color buckets shared by crawl-write normalization (image_qa v17 /
// backfill), the retrieval read paths (image-retrieval, /references colors=
// filter), and the v17 prompt vocabulary — one vocabulary, three consumers,
// zero drift. Moved here from apps/api/src/vision/image-retrieval.ts (0029).
// blush ≠ white by design (the distinction the image-RAG §5 acceptance test
// asserts).

export interface ColorBucket {
  name: string;
  words: string[];
  label: string;
  swatch_hex: string;
}

export const COLOR_BUCKETS: ColorBucket[] = [
  { name: "white", words: ["white", "ivory", "cream", "champagne"], label: "White / Ivory", swatch_hex: "#F8F4EC" },
  { name: "blush", words: ["blush", "pink", "rose pink", "dusty rose"], label: "Pink / Blush", swatch_hex: "#F4C2C2" },
  { name: "red", words: ["red", "crimson", "scarlet"], label: "Red", swatch_hex: "#C0392B" },
  { name: "burgundy", words: ["burgundy", "wine", "marsala", "maroon"], label: "Burgundy / Wine", swatch_hex: "#722F37" },
  { name: "peach", words: ["peach", "coral", "apricot"], label: "Peach / Coral", swatch_hex: "#F7B58C" },
  { name: "yellow", words: ["yellow", "mustard", "lemon"], label: "Yellow", swatch_hex: "#F4D35E" },
  { name: "gold", words: ["gold", "golden", "brass"], label: "Gold", swatch_hex: "#C9A227" },
  { name: "orange", words: ["orange", "terracotta", "rust"], label: "Orange / Terracotta", swatch_hex: "#D9772F" },
  { name: "green", words: ["green", "sage", "eucalyptus", "emerald", "olive"], label: "Green / Sage", swatch_hex: "#7A9B6D" },
  { name: "blue", words: ["blue", "dusty blue", "sky"], label: "Blue", swatch_hex: "#7FA8C9" },
  { name: "navy", words: ["navy", "indigo"], label: "Navy", swatch_hex: "#1F3A5F" },
  { name: "purple", words: ["purple", "lavender", "lilac", "plum", "mauve"], label: "Purple / Lavender", swatch_hex: "#9B7BB8" },
  { name: "black", words: ["black", "noir"], label: "Black", swatch_hex: "#1C1C1C" },
];

/** Closed enum for zod schemas + the image_qa v17 prompt block. */
export const COLOR_BUCKET_NAMES: string[] = COLOR_BUCKETS.map((b) => b.name);

const BUCKET_SET = new Set(COLOR_BUCKET_NAMES);

/**
 * Hex → canonical color bucket via a hue/chroma model (NOT nearest-RGB-centroid,
 * which collapses pale tints into "white"). A near-neutral (tiny chroma) maps by
 * lightness; a tinted color maps by hue, with the pale-vs-deep split that
 * separates blush from red and peach from orange. The pale pink #F8E1E4 →
 * "blush", ivory #FFFFF0 → "white".
 */
export const hexToBucket = (hex: string): string | null => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), delta = max - min;

  // Near-neutral: little chroma → decide by lightness, not hue.
  if (delta < 22) {
    if (max >= 175) return "white";
    if (max <= 70) return "black";
    return null; // mid-grey — no useful bucket, leave to the cross-modal vector
  }

  // Hue in degrees.
  let h: number;
  if (max === r) h = 60 * (((g - b) / delta) % 6);
  else if (max === g) h = 60 * ((b - r) / delta + 2);
  else h = 60 * ((r - g) / delta + 4);
  if (h < 0) h += 360;

  const light = max >= 200; // pale tint vs saturated/deep
  if (h < 15 || h >= 330) return light ? "blush" : (max <= 150 ? "burgundy" : "red");
  if (h < 45) return light ? "peach" : (max <= 150 ? "burgundy" : "orange");
  if (h < 70) return max <= 170 ? "gold" : "yellow";
  if (h < 170) return "green";
  if (h < 255) return "blue";
  if (h < 290) return max <= 110 ? "navy" : "blue";
  return "purple";
};

/** Map a raw palette token (e.g. "cream", "pink") to its canonical bucket. */
export const tokenBucket = (token: string): string => {
  for (const b of COLOR_BUCKETS) {
    if (b.name === token || b.words.includes(token)) return b.name;
  }
  return token;
};

/**
 * Normalize the v17 array-of-pairs (Gemini responseSchema can't express
 * dynamic-key maps) into the canonical species→buckets map stored in
 * vendor_image.flower_colors. Lowercases species, maps every color token
 * through tokenBucket, drops off-vocab tokens and species with zero surviving
 * colors. Pure.
 */
export const normalizeFlowerColors = (
  raw: unknown,
): Record<string, string[]> | null => {
  if (!Array.isArray(raw)) return null;
  const map: Record<string, string[]> = {};
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) continue;
    const species = String((entry as { species?: unknown }).species ?? "").trim().toLowerCase();
    const colors = (entry as { colors?: unknown }).colors;
    if (!species || !Array.isArray(colors)) continue;
    const buckets = [...new Set(
      colors
        .map((c) => tokenBucket(String(c).trim().toLowerCase()))
        .filter((c) => BUCKET_SET.has(c)),
    )];
    if (buckets.length === 0) continue;
    map[species] = [...new Set([...(map[species] ?? []), ...buckets])];
  }
  return Object.keys(map).length > 0 ? map : null;
};

/**
 * Derive the flat bucket list a read path filters on, from whichever color
 * signal a row has: the canonical flower_colors map, or a raw palette array
 * (flower_palette / color_palette tokens) for pre-backfill rows.
 */
export const flatColorBuckets = (
  source: Record<string, string[]> | string[] | null | undefined,
): string[] => {
  if (!source) return [];
  const tokens = Array.isArray(source) ? source : Object.values(source).flat();
  return [...new Set(
    tokens.map((t) => tokenBucket(String(t).trim().toLowerCase())).filter((t) => BUCKET_SET.has(t)),
  )];
};
